import * as React from 'react';
import { Button } from 'reactstrap';
import { useEffect, useState } from 'react';
import { Communicator } from '../../tools/Communication';
import { ContainerType } from '../ContainerComponents/ContainerPage';
import TryNewDesignBanner from './TryNewDesignBanner';
import { Currency, DEFAULT_CURRENCY } from '@/utils/price';
import ContainerPriceTable, {
  ContainerOverduePrice,
  StoreDefaultContainerPrice,
  SystemType,
} from './ContainerPriceTable';
import './ContainerPrices.css';
import { AddContainerTypeModal } from './AddContainerTypeModal';
import {
  defaultNewContainerType,
  type NewContainerType,
} from './newContainerType';
import { useQuery } from '@apollo/client';
import { allUserCountriesQuery } from '../../graphql/queries';
import { UserLanguage } from '@/utils/translation';
import { ContentSection } from '../../components/Layout/ContentSection';
import ContainerTypeFilter from './ContainerTypeFilter';
import { useGetAllContainerTypeGroupsQuery } from '../../graphql/queries/getAllContainerTypeGroups.generated';
import { EditSection } from '../../components/Layout';
import { FaPlus } from 'react-icons/fa';
import Fuse from 'fuse.js';
import { useSyncContainerTypesToZohoMutation } from '../../graphql/mutations/syncContainerTypesToZoho.generated';
import { LayoutStoreInstance } from '../../LayoutStore';
import CustomPagination from '../../components/CustomPagination';
import i18n from '@/utils/i18n';

// Add pagination constants
const PAGE_SIZE = 10; // Show 10 containers per page

export default function EditContainerPrices() {
  const [language, setLanguage] = useState(UserLanguage.De);
  const [storeDefaultContainerPrices, setStoreDefaultContainerPrices] =
    useState<StoreDefaultContainerPrice[]>([]);
  const [touchedContainerTypeIds, setTouchedContainerTypeIds] = useState<
    number[]
  >([]);
  const [allContainerTypes, setAllContainerTypes] = useState<ContainerType[]>(
    [],
  );
  const [consumerOverduePrices, setConsumerOverduePrices] = useState<{
    [currency: string]: { [containerTypeId: number]: ContainerOverduePrice };
  }>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isAddContainerTypeModalOpen, setIsAddContainerTypeModalOpen] =
    useState(false);
  const [newContainerType, setNewContainerType] = useState<NewContainerType>({
    ...defaultNewContainerType,
  });
  const [currencyFilter, setCurrencyFilter] = useState<string[]>([]);
  const [countryFilter, setCountryFilter] = useState<string[]>([]);
  const [classFilter, setClassFilter] = useState<string[]>([]);
  const [groupFilter, setGroupFilter] = useState<string[]>([]);
  const [segmentFilter, setSegmentFilter] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [systemType, setSystemType] = useState<SystemType>('OpenSystem');
  const [containerClasses, setContainerClasses] = useState<
    { value: string; label: string }[]
  >([]);
  // Add pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageCount, setPageCount] = useState(0);

  const [syncContainerTypesToZoho] = useSyncContainerTypesToZohoMutation({
    variables: {
      ids: touchedContainerTypeIds,
    },
    onCompleted: (data) => {
      if (data.syncContainerTypesToZoho) {
        LayoutStoreInstance.setSuccess(
          i18n.t('EditContainerPrices:syncSuccessful'),
        );
      } else {
        LayoutStoreInstance.setError(i18n.t('EditContainerPrices:syncFailed'));
      }
    },
    onError: (error) => {
      LayoutStoreInstance.setError(
        i18n.t('EditContainerPrices:syncFailedWithError', {
          error: error.message,
        }),
      );
    },
  });

  const { data: containerTypeGroups, loading: loadingContainerTypeGroups } =
    useGetAllContainerTypeGroupsQuery({
      fetchPolicy: 'no-cache',
    });

  const { data: countriesData = { __type: { enumValues: [] } } } = useQuery(
    allUserCountriesQuery,
  );

  const countries: { value: string; label: string }[] =
    countriesData.__type.enumValues.map((country: any) => ({
      label: country.name,
      value: country.name,
    }));

  // First, calculate all filtered container types (without pagination)
  const allFilteredContainerTypes = React.useMemo(() => {
    let filtered = allContainerTypes;
    if (searchTerm.length >= 3) {
      const fuse = new Fuse<ContainerType>(allContainerTypes, {
        keys: ['id', 'name', 'sku', 'branding', 'legalOwner'],
        includeScore: true,
        threshold: 0.3,
      });

      const results = fuse.search(searchTerm);
      filtered = results.map((result) => result.item);
    }
    if (!Number.isNaN(parseInt(searchTerm))) {
      const id = parseInt(searchTerm);
      filtered = filtered.filter((ct) => ct.id === id);
    }
    filtered = filtered
      .filter((ct) => {
        if (
          countryFilter.length > 0 &&
          !countryFilter.some((country) =>
            (ct.countryCodes || []).includes(country),
          )
        ) {
          return false;
        }
        if (classFilter.length > 0 && !classFilter.includes(ct.class)) {
          return false;
        }
        if (
          groupFilter.length > 0 &&
          !groupFilter.includes(ct.groupId.toString())
        ) {
          return false;
        }

        if (
          segmentFilter.length > 0 &&
          (!ct.segment || !segmentFilter.includes(ct.segment))
        ) {
          return false;
        }

        return true;
      })
      .sort((a, b) => b.orderKey - a.orderKey);

    return filtered;
  }, [
    allContainerTypes,
    countryFilter,
    classFilter,
    groupFilter,
    segmentFilter,
    searchTerm,
  ]);

  // Calculate page count when filtered results change
  React.useEffect(() => {
    const totalPages = Math.ceil(allFilteredContainerTypes.length / PAGE_SIZE);
    setPageCount(totalPages);
    console.log({
      filteredLength: allFilteredContainerTypes.length,
      totalPages,
      classFilter,
    });
  }, [allFilteredContainerTypes.length, classFilter]);

  // Then apply pagination to get the current page's container types
  const filteredContainerTypes = React.useMemo(() => {
    const startIndex = (currentPage - 1) * PAGE_SIZE;
    const endIndex = startIndex + PAGE_SIZE;
    return allFilteredContainerTypes.slice(startIndex, endIndex);
  }, [allFilteredContainerTypes, currentPage]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [countryFilter, classFilter, groupFilter, segmentFilter, searchTerm]);

  // Performance monitoring
  const totalContainers = allContainerTypes.length;
  const totalCurrencies = Object.keys(Currency).length;
  const activeCurrencies =
    currencyFilter.length > 0 ? currencyFilter.length : totalCurrencies;
  const visibleContainers = filteredContainerTypes.length;
  const totalFilteredContainers = allFilteredContainerTypes.length;
  const estimatedInputs = visibleContainers * activeCurrencies * 6;

  // Performance warning component
  const PerformanceWarning = () => {
    const isHighLoad = estimatedInputs > 600; // Warn if more than 600 inputs (10 containers × 15 currencies × 4 fields)

    if (!isHighLoad) return null;

    return (
      <div className="alert alert-warning mb-3">
        <strong>⚠️ {i18n.t('EditContainerPrices:performanceWarning')}</strong>{' '}
        {i18n.t('EditContainerPrices:performanceWarningText', {
          count: estimatedInputs,
        })}
        <ul className="mb-0 mt-2">
          <li>
            {i18n.t('EditContainerPrices:useCurrencyFilters', {
              active: activeCurrencies,
              total: totalCurrencies,
            })}
          </li>
          <li>
            {i18n.t('EditContainerPrices:useSearchFilters', {
              visible: totalFilteredContainers,
              total: totalContainers,
            })}
          </li>
          <li>{i18n.t('EditContainerPrices:workWithFewerCurrencies')}</li>
        </ul>
      </div>
    );
  };

  useEffect(() => {
    if (systemType === 'OpenSystem') {
      setClassFilter([]);
    } else if (systemType === 'Event') {
      setClassFilter(['EventPortfolio']);
    }
  }, [systemType]);

  useEffect(() => {
    load();
  }, []);

  async function load() {
    const promises = [
      Communicator.Get(
        '/Container/GetAllOverduePricesByContainerTypeIdByCurrency',
        {},
      )
        .then((response) => response.json())
        .then((dict) => setConsumerOverduePrices(dict)),
      Communicator.Get('/Config/GetConfigValue', {
        key: 'DefaultContainerInformation',
      })
        .then((response) => response.json())
        .then((prices: StoreDefaultContainerPrice[]) =>
          setStoreDefaultContainerPrices(
            prices.map(expandPrice).sort((a: any, b: any) => a.Id - b.Id),
          ),
        ),
      Communicator.Get('/Container/GetAllContainerCategories', {})
        .then((response) => response.json())
        .then((data: { containerTypes: ContainerType[] }) => {
          if (data.containerTypes) {
            // assume that container types have ids from 1 to n without 'holes'
            setAllContainerTypes(
              [...data.containerTypes].sort((a, b) => a.id - b.id),
            );
          }
        }),
      Communicator.Get('/Container/GetAllContainerClasses', {})
        .then((response) => response.json())
        .then((containerClasses: { id: string; name: string }[]) =>
          setContainerClasses(
            containerClasses.map((cc) => ({ label: cc.name, value: cc.name })),
          ),
        ),
    ];
    await Promise.all(promises);
    setTouchedContainerTypeIds([]);
    setIsLoading(false);
  }
  /**
   * Add all currencies to price (optimized to only expand needed currencies)
   * @param price
   * @returns
   */
  function expandPrice(price: StoreDefaultContainerPrice) {
    const defaultPrice = {
      TransactionPrice: '0',
      StoreLostCost: '0',
      StoreLostCostLower: '0',
      StoreLostCostUpper: '0',
      RentalPrice: '0',
      CleaningPrice: '0',
      LostPrice: '0',
      DepositPrice: '0',
      CostPrice: '0',
      LogisticPartnerCleaningPrice: '0',
    };
    price[DEFAULT_CURRENCY] = {
      ...defaultPrice,
      ...(price[DEFAULT_CURRENCY] || {}),
    };
    for (const c of Object.keys(Currency)) {
      price[c] = { ...price[DEFAULT_CURRENCY], ...(price[c] || {}) };
    }
    return price;
  }

  async function savePrices() {
    setIsLoading(true);
    await Promise.all([
      saveStoreDefaultContainerPrices(),
      saveConsumerOverdueContainerPrices(),
    ])
      .then(() => syncContainerTypesToZoho())
      .then(async () => load());
  }

  async function saveConsumerOverdueContainerPrices() {
    try {
      const allOverduePrices: any[] = [];
      for (const priceByContainerType of Object.values(consumerOverduePrices)) {
        for (const price of Object.values(priceByContainerType)) {
          if (price.containerTypeId !== -1) {
            allOverduePrices.push({
              ...price,
              priceInclVatInCents: parseInt(price.priceInclVatInCents),
              creditsOnReturnInCents: parseInt(price.creditsOnReturnInCents),
            });
          }
        }
      }
      const result = await Communicator.PostData(
        '/Container/SetOverduePrices',
        allOverduePrices,
      );
      setConsumerOverduePrices(await result.json());
    } catch (e) {
      console.error(e);
      alert(i18n.t('EditContainerPrices:errorSavingOverduePrices'));
    }
  }

  async function saveStoreDefaultContainerPrices() {
    try {
      const newInfos: any[] = [...storeDefaultContainerPrices].map((info) => {
        const newInfo: any = { ...info };
        for (const key of Object.keys(newInfo)) {
          if (key !== 'Id') {
            newInfo[key] = {
              TransactionPrice: parseFloat(
                newInfo[key].TransactionPrice || '0',
              ),
              StoreLostCost: parseInt(newInfo[key].StoreLostCost || '0'),
              StoreLostCostUpper: parseInt(
                newInfo[key].StoreLostCostUpper || '0',
              ),
              StoreLostCostLower: parseInt(
                newInfo[key].StoreLostCostLower || '0',
              ),
              RentalPrice: parseInt(newInfo[key].RentalPrice || '0'),
              CleaningPrice: parseInt(newInfo[key].CleaningPrice || '0'),
              LostPrice: parseInt(newInfo[key].LostPrice || '0'),
              DepositPrice: parseInt(newInfo[key].DepositPrice || '0'),
              CostPrice: parseInt(newInfo[key].CostPrice || '0'),
              LogisticPartnerCleaningPrice: parseInt(
                newInfo[key].LogisticPartnerCleaningPrice || '0',
              ),
            };
          }
        }
        return newInfo;
      });
      const result = await Communicator.PostData('/Config/SetConfigValue', {
        Key: 'DefaultContainerInformation',
        Value: JSON.stringify(newInfos),
      });
      setStoreDefaultContainerPrices(await result.json());
    } catch (e) {
      console.error(e);
      alert(i18n.t('EditContainerPrices:errorSavingConfig'));
    }
  }

  // Pagination info component
  const renderPaginationInfo = () => {
    if (pageCount <= 1) return null;
    const totalFiltered = allFilteredContainerTypes.length;
    const startItem = Math.min(
      (currentPage - 1) * PAGE_SIZE + 1,
      totalFiltered,
    );
    const endItem = Math.min(currentPage * PAGE_SIZE, totalFiltered);

    return (
      <div className="mt-2 text-center">
        <small className="text-muted">
          {i18n.t('showing')} {startItem} {i18n.t('to')} {endItem}{' '}
          {i18n.t('of')} {totalFiltered} {i18n.t('containers')}
          {totalFiltered !== allContainerTypes.length && (
            <span>
              {' '}
              ({i18n.t('filtered from')} {allContainerTypes.length}{' '}
              {i18n.t('total')})
            </span>
          )}
        </small>
      </div>
    );
  };

  if (isLoading || loadingContainerTypeGroups) {
    return <p>{i18n.t('Common:loading')}</p>;
  }

  return (
    <ContentSection
      title={i18n.t('EditContainerPrices:title')}
      collapsable={false}
    >
      <TryNewDesignBanner />
      <div className="input-group mb-3">
        <span className="input-group-text" id="basic-addon1">
          {i18n.t('EditContainerPrices:selectLanguageLabel')}
        </span>
        <select
          className="form-control custom-select"
          onChange={(e) => setLanguage(e.target.value)}
          value={language}
        >
          {Object.keys(UserLanguage).map((k) => (
            <option key={k}>{k}</option>
          ))}
        </select>
      </div>
      <div className="input-group mb-3">
        <span className="input-group-text" id="basic-addon1">
          {i18n.t('EditContainerPrices:selectSystemTypeLabel')}
        </span>
        <select
          className="form-control custom-select"
          onChange={(e) => setSystemType(e.target.value as any)}
          value={systemType}
        >
          <option>{i18n.t('EditContainerPrices:openSystem')}</option>
          <option>{i18n.t('EditContainerPrices:event')}</option>
        </select>
      </div>
      <ContainerTypeFilter
        countryFilter={countryFilter}
        setCountryFilter={setCountryFilter}
        classFilter={classFilter}
        setClassFilter={setClassFilter}
        groupFilter={groupFilter}
        setGroupFilter={setGroupFilter}
        segmentFilter={segmentFilter}
        setSegmentFilter={setSegmentFilter}
        containerTypeGroups={
          containerTypeGroups?.getAllContainerTypeGroups.map((g) => ({
            value: g.id.toString(),
            label: g.name,
          })) || []
        }
        containerClasses={containerClasses}
        segments={[
          { value: 'Drinks', label: i18n.t('EditContainerInfo:segmentDrinks') },
          { value: 'Food', label: i18n.t('EditContainerInfo:segmentFood') },
          {
            value: 'Cutlery',
            label: i18n.t('EditContainerInfo:segmentCutlery'),
          },
        ]}
        countries={countries}
        currencies={Object.keys(Currency).map((c) => ({
          value: c,
          label: c,
        }))}
        currencyFilter={currencyFilter}
        setCurrencyFilter={setCurrencyFilter}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
      />
      <EditSection
        title={i18n.t('EditContainerPrices:containerTypePrices')}
        className="container-types"
        onSave={savePrices}
        isLoading={isLoading}
      >
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div></div>
          <Button
            color="primary"
            disabled={isLoading}
            onClick={() => setIsAddContainerTypeModalOpen(true)}
          >
            <FaPlus className="me-1" />{' '}
            {i18n.t('EditContainerPrices:addContainerType')}
          </Button>
        </div>

        {/* Add performance info */}
        <div className="alert alert-info mb-3">
          <small>
            <strong>{i18n.t('EditContainerPrices:performanceTip')}</strong>{' '}
            {i18n.t('EditContainerPrices:performanceTipText', {
              pageSize: PAGE_SIZE,
            })}
            <br />
            Currently rendering: {estimatedInputs.toLocaleString()} input fields
            ({visibleContainers} containers × {activeCurrencies} currencies)
          </small>
        </div>

        <PerformanceWarning />

        <ContainerPriceTable
          storeDefaultContainerPrices={storeDefaultContainerPrices}
          containerTypes={filteredContainerTypes}
          consumerOverduePrices={consumerOverduePrices}
          currencyFilter={currencyFilter}
          setStoreDefaultContainerPrices={setStoreDefaultContainerPrices}
          setConsumerOverduePrices={setConsumerOverduePrices}
          language={language}
          systemType={systemType}
          setTouchedContainerTypeIds={setTouchedContainerTypeIds}
        />

        <div className="d-flex justify-content-center mt-3">
          <CustomPagination
            currentPage={currentPage}
            pageCount={pageCount}
            onPageChange={setCurrentPage}
            maxPagesToShow={10}
          />
        </div>
        {renderPaginationInfo()}
      </EditSection>
      <AddContainerTypeModal
        isAddContainerTypeModalOpen={isAddContainerTypeModalOpen}
        setIsAddContainerTypeModalOpen={setIsAddContainerTypeModalOpen}
        newContainerType={newContainerType}
        setNewContainerType={setNewContainerType}
        currency={DEFAULT_CURRENCY}
        setIsLoading={setIsLoading}
        load={load}
      />
    </ContentSection>
  );
}
